/**
 * scrollToHash — scroll an in-page section into view under the sticky header.
 * ─────────────────────────────────────────────────────────────────────────
 *   scrollToHash('#faq')            → smooth-scrolls so #faq sits 16px under the header
 *   scrollToHash('#faq', { wait })  → retries until the element exists (lazy routes), max `wait` ms
 *
 * A <details id="…"> target is opened. The offset is the height of the
 * sticky V2 header (`.sh`, or an app `.top` bar) when one is on the page.
 * Returns a cancel function (only meaningful with `wait`).
 */
export function headerOffset() {
  if (typeof document === 'undefined') return 0;
  const bar = document.querySelector('.sh') || document.querySelector('.top');
  return bar ? bar.getBoundingClientRect().height : 0;
}

export function scrollToHash(hash, { wait = 0, behavior = 'smooth', gap = 16 } = {}) {
  if (typeof window === 'undefined' || !hash) return () => {};
  let id = String(hash).replace(/^#/, '');
  try { id = decodeURIComponent(id); } catch (_) { /* keep raw */ }
  if (!id) return () => {};

  let cancelled = false;
  let timer = 0;
  const started = Date.now();

  const attempt = () => {
    if (cancelled) return;
    const el = document.getElementById(id);
    if (!el) {
      if (Date.now() - started < wait) timer = window.setTimeout(attempt, 80);
      return;
    }
    if (el.tagName === 'DETAILS') el.open = true;
    const y = Math.max(0, el.getBoundingClientRect().top + window.scrollY - headerOffset() - gap);
    window.scrollTo({ top: y, behavior });
  };

  attempt();
  return () => { cancelled = true; window.clearTimeout(timer); };
}

export default scrollToHash;
